/**
 * Which lane a reminder contract belongs to on /today.
 *
 * The 2026-09-12 mockup draws reminders in two places: a thin bar under the
 * header for what is held to a CLOCK today ("Take trash out · Due 18:00 ·
 * Done · Snooze"), and a quiet card for what is held to the DAY with no
 * clock ("Check tire pressure"). A contract on a later day belongs to that
 * day and to /schedule, not to this morning (owner, 2026-09-12: the strip
 * "should lift the items due for today").
 *
 * Pure and import-free so the node probe (`npm run test:ori`) can pin it.
 * Every function takes `now` so the probe is deterministic; callers pass
 * nothing and get the wall clock.
 */

export interface LaneContract {
  overdue?: boolean | null
  op_state?: string | null
  awaiting_reason?: string | null
  next_fire_at?: string | null
  deadline_at?: string | null
  bound_due_date?: string | null
  bound_due_time?: string | null
  stated_due_date?: string | null
}

export type ContractLane = 'due' | 'allday' | 'later'

/** Local calendar day of an instant, as YYYY-MM-DD. */
export function localDayOf(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Local "HH:MM" of an instant — the same 24h shape every clock on /today uses. */
export function localClockOf(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function dueNow(c: LaneContract, now: number = Date.now()): boolean {
  return !!c.next_fire_at && new Date(c.next_fire_at).getTime() <= now
}

/**
 * Which day a contract belongs to. 'today' also covers what is overdue or
 * unplaced-but-yours (an ask with no day yet needs your decision, so it
 * stays in front of you); 'later' is a contract whose deadline, bound task,
 * or stated day is on a later day.
 */
export function contractDay(c: LaneContract, today: string, now: number = Date.now()): 'today' | 'later' {
  if (c.overdue || dueNow(c, now) || c.op_state === 'firing') return 'today'
  const named = c.bound_due_date || c.stated_due_date
  const day = named
    ? String(named).slice(0, 10)
    : c.deadline_at ? localDayOf(c.deadline_at)
    : c.next_fire_at ? localDayOf(c.next_fire_at)
    : null
  if (day === null) return 'today'                 // no day at all: yours to place
  return day > today ? 'later' : 'today'
}

export interface ContractClock {
  /** "HH:MM", local. */
  clock: string
  /** What the clock IS: the time it is due, the time it next reminds, or
   *  the time a snooze ends. The bar says which, never a bare number. */
  kind: 'due' | 'next' | 'snoozed'
}

/**
 * The clock a contract is held to, or null when it has none (held to the
 * day only). Precedence: the deadline the person set or the task carries,
 * then the bound task's own due time, then the next reminder. A snoozed
 * contract's clock is the end of the snooze, whatever else it carries — that
 * is the fact the person just chose.
 */
export function contractClock(c: LaneContract): ContractClock | null {
  if (c.op_state === 'snoozed' && c.next_fire_at) return { clock: localClockOf(c.next_fire_at), kind: 'snoozed' }
  if (c.deadline_at) return { clock: localClockOf(c.deadline_at), kind: 'due' }
  if (c.bound_due_time && /^\d{1,2}:\d{2}/.test(String(c.bound_due_time))) {
    const [h, m] = String(c.bound_due_time).split(':')
    return { clock: `${h.padStart(2, '0')}:${m.slice(0, 2)}`, kind: 'due' }
  }
  if (c.next_fire_at) return { clock: localClockOf(c.next_fire_at), kind: 'next' }
  return null
}

/** due = today and on a clock · allday = today, no clock · later = not today. */
export function contractLane(c: LaneContract, today: string, now: number = Date.now()): ContractLane {
  if (contractDay(c, today, now) === 'later') return 'later'
  return contractClock(c) ? 'due' : 'allday'
}

/** "today" · "tomorrow" · "yesterday" · a weekday within the week · "Sep 20". */
export function dayWord(dayISO: string, today: string): string {
  const [y, m, d] = dayISO.split('-').map(Number)
  const [ty, tm, td] = today.split('-').map(Number)
  const diff = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86_400_000)
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff === -1) return 'yesterday'
  const dt = new Date(y, m - 1, d)
  return dt.toLocaleDateString(undefined, diff > 0 && diff < 7 ? { weekday: 'long' } : { month: 'short', day: 'numeric' })
}
